import { Loading3QuartersOutlined, WarningFilled } from '@ant-design/icons'
import { DatePicker, Divider, Flex, Form, Input, InputNumber, Modal, Switch } from 'antd'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { simpleNotification } from '@halo/_utils/notifications'
import { numberAsCurrency, numberWithCommas } from '@halo/_utils/numeral'
import './account-charge-form.scss'
import dayjs, { Dayjs } from 'dayjs'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { AccountBillingRateType } from '@halo/enums/account-billing-rate-type'
import { AccountBillingType } from '@halo/enums/account-billing-type'
import { GetBillableAccountResponse, BillingDataResponse } from '@pushly/models/lib/structs'

interface IAccountChargeFormProps {
    account: GetBillableAccountResponse
    visible: boolean
    onClose: (e: any) => void
    onSuccess: (charge: any) => void
    defaultBillingPeriod?: string
}

interface IAccountChargeFormValues {
    billablePeriod?: Dayjs
    override?: number
    lidOverride?: string
    notes?: string
}

const formItemLayout = {
    labelCol: { xs: { span: 24 }, sm: { span: 6 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 18 } },
}

export const AccountChargeForm: React.FC<IAccountChargeFormProps> = (props) => {
    const appService = useMemo(() => Container.get(AppService), [])
    const accountService = useMemo(() => Container.get(AccountService), [])

    const [form] = Form.useForm<IAccountChargeFormValues>()
    const billablePeriod = Form.useWatch('billablePeriod', form)

    const [overrideEnabled, setOverrideEnabled] = useState(false)
    const [loadingSelectedBillablePeriodSubs, setLoadingSelectedBillablePeriodSubs] = useState(false)
    const [selectedBillablePeriodSubs, setSelectedBillablePeriodSubs] = useState<number | undefined>(undefined)
    const [selectedBillablePeriodAlreadyBilled, setSelectedBillablePeriodAlreadyBilled] = useState(false)
    const [billingRecords, setBillingRecords] = useState<any[] | undefined>(undefined)
    const [lastBillingRecord, setLastBillingRecord] = useState<any>(undefined)

    const billingData: BillingDataResponse = props.account?.billing_data || new BillingDataResponse()

    const billingType: AccountBillingType = AccountBillingType.fromString(billingData.type || 'INVOICE')

    const rateType: AccountBillingRateType | undefined = billingData.rate_type
        ? AccountBillingRateType.fromString(billingData.rate_type)
        : undefined

    const lastMonthValue: Dayjs = dayjs().subtract(1, 'month').startOf('month')

    const isCredit = billingType === AccountBillingType.CREDIT

    const currencyDisplayName = (() => {
        const currency = (billingData.currency || 'USD').toUpperCase()
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === currency)?.label || 'USD'
    })()

    const currencySymbol = (() => {
        const currency = (billingData.currency || 'USD').toUpperCase()
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === currency)?.symbol || '$'
    })()

    const rateTypeDisplayName = (() => {
        switch (rateType) {
            case AccountBillingRateType.FLAT_RATE:
                return 'Flat Monthly Rate'
            case AccountBillingRateType.VARIABLE_SUBSCRIBER:
                return 'Variable Subscriber Rate'
            case AccountBillingRateType.REVSHARE_PV:
                return 'Revenue Share (PV)'
            default:
                return 'Unknown'
        }
    })()

    const rateUnitLabel = rateType === AccountBillingRateType.FLAT_RATE ? 'month' : 'subscriber'

    const expectedChargeValue: number | undefined = (() => {
        const { rate } = billingData
        const subs = selectedBillablePeriodSubs || 0

        if (subs <= 0 || rate === undefined) {
            return undefined
        }

        if (rateType === AccountBillingRateType.VARIABLE_SUBSCRIBER) {
            return subs * rate
        }

        return rate
    })()

    const lineItemDescription = `Pushly Monthly Fee - ${numberWithCommas(selectedBillablePeriodSubs)} Subscribers`

    const toggleOverrideEnabled = (next: boolean) => {
        setOverrideEnabled(next)
        if (next) {
            form.setFieldsValue({ override: expectedChargeValue, lidOverride: lineItemDescription })
        } else {
            form.resetFields(['override', 'lidOverride'])
        }
    }

    const renderLastBilledAt = () => {
        if (!lastBillingRecord) {
            return '--'
        }
        return dayjs(lastBillingRecord.created_at).format('LL')
    }

    const renderExpectedCharge = () => {
        const symbol = currencySymbol
        const value = expectedChargeValue === undefined ? '--' : expectedChargeValue

        return !selectedBillablePeriodSubs ? '--' : `${symbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    const clearForm = useCallback(() => {
        form.resetFields(['override', 'lidOverride', 'notes'])
        setOverrideEnabled(false)
        setSelectedBillablePeriodSubs(undefined)
    }, [form])

    const updateSelectedPeriodBillableSubs = useCallback(
        async (period: Dayjs | undefined): Promise<void> => {
            setLoadingSelectedBillablePeriodSubs(true)

            let alreadyBilled = false
            if (billingRecords && period) {
                const existing = billingRecords.find(
                    (r: any) => r.metadata.billable_period === period.format('MMMM, YYYY'),
                )
                if (existing) {
                    alreadyBilled = true
                }
            }
            setSelectedBillablePeriodAlreadyBilled(alreadyBilled)

            // mirror legacy clear of dependent fields & override state
            form.resetFields(['override', 'lidOverride', 'notes'])
            setOverrideEnabled(false)
            setSelectedBillablePeriodSubs(undefined)

            let nextSubs: number | undefined

            if (period) {
                const dateRange = {
                    since: period.startOf('month').format('YYYY-MM-DD'),
                    through: period.endOf('month').format('YYYY-MM-DD'),
                }

                const { ok, data } = await accountService.fetchSubscriberHistoryByAccountId(
                    props.account.id,
                    { date_range: JSON.stringify(dateRange) },
                    false,
                    `charge-sub-history-${props.account.id}`,
                )

                if (ok && data.length > 0) {
                    nextSubs = data[0].subscribed_count || 0
                }
            }

            setSelectedBillablePeriodSubs(nextSubs)
            setLoadingSelectedBillablePeriodSubs(false)
        },
        [accountService, billingRecords, form, props.account?.id],
    )

    const handleSubmit = async () => {
        const values = form.getFieldsValue()

        const recordDto: any = {
            billable_period: values.billablePeriod!.format('MMMM, YYYY'),
            description: lineItemDescription,
            amount: expectedChargeValue,
            notes: values.notes,
        }

        if (values.override) {
            recordDto.amount = values.override
            recordDto.description = values.lidOverride
            recordDto.original_amount = expectedChargeValue
        }

        const record = await accountService.createBillingRecordForAccountId(props.account.id, recordDto)

        if (record.ok) {
            Modal.confirm({
                title: 'Confirm Payment',
                content: (
                    <div>
                        <p>Please confirm the following payment details for account: {props.account.name}</p>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Billable Period</div>
                            <div className={classnames('form-layout-row-value')}>{recordDto.billable_period}</div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Billable Subs</div>
                            <div className={classnames('form-layout-row-value')}>
                                {numberWithCommas(selectedBillablePeriodSubs)}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Description</div>
                            <div className={classnames('form-layout-row-value')}>{recordDto.description}</div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Amount</div>
                            <div className={classnames('form-layout-row-value')}>
                                {currencySymbol}
                                {numberAsCurrency(recordDto.amount)}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Notes</div>
                            <div className={classnames('form-layout-row-value')}>{recordDto.notes || 'None'}</div>
                        </div>
                    </div>
                ),
                onOk: async () => {
                    const confirmation = await accountService.confirmBillingRecord(record.data.confirmation_endpoint)
                    const stripeError = confirmation.data?.metadata?.stripe_error

                    if (confirmation.ok) {
                        props.onClose(null)
                        if (stripeError) {
                            simpleNotification(
                                'error',
                                `The requested charge for, ${props.account.name}, has been declined with the following error: ${stripeError.message}`,
                            )
                        } else {
                            simpleNotification(
                                'success',
                                `Account, ${props.account.name}, has been successfully charged.`,
                            )
                        }
                    } else {
                        simpleNotification('error', 'An error occurred during payment processing.')
                    }

                    Modal.destroyAll()
                },
            })
        } else {
            simpleNotification('error', 'An error occurred during payment record creation.')
        }
    }

    const handleDrawerSubmit = async () => {
        try {
            await form.validateFields()
            await handleSubmit()
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const handleClose = async () => {
        clearForm()
        props.onClose(null)
    }

    // ensureRequiredDepedencies — mount + props.account ready
    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (!props.account) return

            if (props.defaultBillingPeriod) {
                form.setFieldsValue({ billablePeriod: dayjs(props.defaultBillingPeriod) })
                updateSelectedPeriodBillableSubs(dayjs(props.defaultBillingPeriod))
            }

            const { ok, data } = await accountService.fetchBillingRecordsByAccountId(props.account.id, {
                order: JSON.stringify({ field: 'record.created_at', direction: 'DESC' }),
                processed: 1,
            })

            if (!cancelled && props.account && ok && data.length > 0) {
                setBillingRecords(data)
                setLastBillingRecord(data[0])
            }
        }

        run()

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.account?.id])

    return (
        <Drawer
            className={classnames('adf-billing-editor')}
            title={`Payment ${props.visible && `- ${props.account.name}`}`}
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleDrawerSubmit}
            submitText="Submit Payment"
            onClose={handleClose}
            open={props.visible}
        >
            {props.visible && (
                <>
                    <Well title="Payment Profile" footerMode="hidden" padding="none">
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Customer Reference ID</div>
                            <div className={classnames('form-layout-row-value')}>{billingData.stripe_customer_id}</div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Contact Email</div>
                            <div className={classnames('form-layout-row-value')}>
                                {billingData.stripe_customer_email}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Current Credit Card</div>
                            <div className={classnames('form-layout-row-value')}>
                                **** **** **** {billingData.stripe_customer_last4}
                            </div>
                        </div>
                    </Well>

                    <Divider />

                    <Well hideHeader={true} footerMode="hidden" padding="none">
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Billing Type</div>
                            <div className={classnames('form-layout-row-value')}>
                                {isCredit ? 'Credit Card' : 'Invoicing'}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Currency</div>
                            <div className={classnames('form-layout-row-value')}>{currencyDisplayName}</div>
                        </div>

                        {isCredit && (
                            <>
                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Rate</div>
                                    <div className={classnames('form-layout-row-value')}>{rateTypeDisplayName}</div>
                                </div>

                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Value</div>
                                    <div className={classnames('form-layout-row-value')}>
                                        {currencySymbol}
                                        {billingData.rate} / {rateUnitLabel}
                                    </div>
                                </div>
                            </>
                        )}
                    </Well>

                    <Divider />

                    <Well hideHeader={true} footerMode="hidden" padding="none">
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Last Billed</div>
                            <div className={classnames('form-layout-row-value')}>{renderLastBilledAt()}</div>
                        </div>
                    </Well>

                    <Divider />

                    <Well title="Make a Charge" footerMode="hidden" padding="none">
                        <Form
                            className={classnames('acf-form', 'hide-inline-errors')}
                            form={form}
                            onFinish={handleSubmit}
                            size="small"
                            labelAlign="left"
                            requiredMark={false}
                            {...formItemLayout}
                        >
                            <Form.Item
                                className={classnames('acf-form-billable-period')}
                                label="Billable Period"
                                name="billablePeriod"
                                rules={[{ required: true, message: 'You must select a billable period.' }]}
                            >
                                <DatePicker.MonthPicker
                                    placeholder=""
                                    format="MMMM, YYYY"
                                    disabledDate={(current: Dayjs) =>
                                        current.startOf('month').valueOf() > lastMonthValue.valueOf()
                                    }
                                    onChange={updateSelectedPeriodBillableSubs}
                                />
                            </Form.Item>

                            {selectedBillablePeriodAlreadyBilled && (
                                <div className={classnames('acf-form-billable-period-error')}>
                                    <WarningFilled />
                                    <span>Selected period has already been billed.</span>
                                </div>
                            )}

                            {billablePeriod != null && (
                                <>
                                    <Form.Item className={classnames('acf-form-calc-amount')} label="Billable Subs">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            numberWithCommas(selectedBillablePeriodSubs)
                                        )}
                                    </Form.Item>
                                    <Form.Item className={classnames('acf-form-calc-amount')} label="Calculated Amount">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            renderExpectedCharge()
                                        )}
                                    </Form.Item>
                                    <Form.Item label="Description">
                                        {loadingSelectedBillablePeriodSubs ? (
                                            <Loading3QuartersOutlined spin={true} />
                                        ) : (
                                            lineItemDescription
                                        )}
                                    </Form.Item>

                                    {!loadingSelectedBillablePeriodSubs && (
                                        <>
                                            <Form.Item label="Override">
                                                <Flex gap={8} align="center">
                                                    <Switch
                                                        checked={overrideEnabled}
                                                        onChange={toggleOverrideEnabled}
                                                    />

                                                    {overrideEnabled && (
                                                        <Form.Item name="override" noStyle={true}>
                                                            <InputNumber
                                                                min={1}
                                                                formatter={(v) =>
                                                                    `${currencySymbol} ${v}`.replace(
                                                                        /\B(?=(\d{3})+(?!\d))/g,
                                                                        ',',
                                                                    )
                                                                }
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </Flex>
                                            </Form.Item>

                                            {overrideEnabled && (
                                                <Form.Item
                                                    label="Description"
                                                    name="lidOverride"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                'A description must be provided for charge overrides.',
                                                        },
                                                    ]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            )}

                                            <Form.Item
                                                label="Notes"
                                                name="notes"
                                                rules={[
                                                    {
                                                        required: overrideEnabled,
                                                        message: 'A reason must be provided for charge overrides.',
                                                    },
                                                ]}
                                            >
                                                <Input.TextArea rows={2} autoSize={false} />
                                            </Form.Item>
                                        </>
                                    )}
                                </>
                            )}
                        </Form>
                    </Well>
                </>
            )}
        </Drawer>
    )
}
